import React, { useContext, useRef, useState } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';

import { Image } from 'semantic-ui-react';

import classNames from 'classnames';

import '../css/StudentCourseDrivenCarousel.less';

import courseDefault from '../img/default-course.svg';

import iconChevronLeft from '../img/icon-chevron-left.svg';
import iconChevronRight from '../img/icon-chevron-right.svg';

import { SatCoreRegister } from '../SatCoreRegistry';

import { CONTENT_ITEM_TYPES } from '../managers/ContentManager';

import UtilityService from '../services/UtilityService';

import PopupService from '../services/PopupService';

const StudentCourseDrivenCarousel = observer((props = {}) => {
  const {
    courseList = [],
    t
  } = props;

  const {
    accessManager,
    assignmentManager,
    studentCourseDrivenExperienceManager
  } = useContext(MobXProviderContext);

  const { selectedCourseDrivenCarouselIndex: selectedCourseIndex } = studentCourseDrivenExperienceManager;

  const loading = studentCourseDrivenExperienceManager.loadingStudentCourseDrivenExperience;

  const studentCourseDrivenCarouselRef = useRef();

  const [isTransitioning, setIsTransitioning] = useState(false);

  const noCoursesAvailable = !!(!courseList?.length || typeof selectedCourseIndex !== 'number');

  const showCarouselSliderNavButtons = !!(courseList?.length > 1);

  const selectedCourse = !noCoursesAvailable ? courseList[selectedCourseIndex] : {};

  // selected course name
  const courseNameObj = UtilityService.reactHtmlParserWrapper(selectedCourse.friendlyName || selectedCourse.name);
  const strippedCourseName = courseNameObj?.stripped || '';
  const parsedCourseName = courseNameObj?.parsed || '';

  // Navigation functions
  const goToPrevious = () => {
    navigateCarousel('previous');
  };

  const goToNext = () => {
    navigateCarousel('next');
  };

  const navigateCarousel = (direction) => {
    if (courseList.length === 0 || isTransitioning || loading) {
      return;
    }
    // Start carousel transition effects
    setIsTransitioning(true);
    // Apply transition classes to the whole carousel
    const carouselElement = studentCourseDrivenCarouselRef.current;
    carouselElement.classList.add('transition-active', 'transition-out');
    // Wait for transition-out to complete before changing index
    setTimeout(async () => {
      let newIndex;
      if (direction === 'next') {
        newIndex = selectedCourseIndex === courseList.length - 1 ? 0 : selectedCourseIndex + 1;
      } else {
        newIndex = selectedCourseIndex === 0 ? courseList.length - 1 : selectedCourseIndex - 1;
      }

      if (typeof props.handleChangeSelectedCourse === 'function') {
        await props.handleChangeSelectedCourse({ courseIndex: newIndex });
      }

      // Content change happens between transition-out and transition-in states
      setTimeout(() => {
        carouselElement.classList.remove('transition-out');
        carouselElement.classList.add('transition-in');
        setTimeout(() => {
          setIsTransitioning(false);
          carouselElement.classList.remove('transition-active', 'transition-in');
        }, 300);
      }, 50);
    }, 300);
  };

  const getBookCardContent = () => {
    const shouldDisableBookImageCard = accessManager?.policies?.studentDashboard?.bookImageCard === false;

    const { customDefaultAssignmentCardImg: customDefaultCardImg } = assignmentManager;

    // define course image
    let selectedCourseImage = null;
    if (selectedCourse?.imageUrl) {
      selectedCourseImage = (
        <Image alt={selectedCourse?.altText ? selectedCourse.altText : ''}
          fluid src={CONTENT_ITEM_TYPES.getContentImageUrl(selectedCourse.imageUrl)} />
      );
    } else if (customDefaultCardImg) {
      selectedCourseImage = <Image fluid src={customDefaultCardImg} />;
    }

    return (
      <div className='student-course-driven-carousel-inner'>
        <div className={classNames('student-course-driven-carousel-image-wrapper', {
          'default-image': !selectedCourseImage
        })}>
          {selectedCourseImage || <Image src={courseDefault} />}
        </div>

        <div className='w-content w-content-book-card-container student-course-driven-carousel-title-wrapper'>
          <div className='student-course-driven-carousel-slider'>
            {showCarouselSliderNavButtons && (
              <button aria-label={t('previousCourse')}
                className={classNames('nav-button', 'prev-button', {
                  'nav-button-wait': loading || isTransitioning
                })}
                onClick={goToPrevious}
                type='button'>
                <Image src={iconChevronLeft} />
              </button>
            )}
            <div key={`course_${selectedCourse.id}`}
              className={classNames('book-card-container course-driven-carousel-card-container', {
                disabled: shouldDisableBookImageCard
              })} id={selectedCourse.id}>
              {PopupService.renderPopup({
                className: 'carousel-course-name-popup',
                content: (
                  <div className='course-name'>
                    {parsedCourseName}
                  </div>
                ),
                disabled: noCoursesAvailable,
                mouseEnterDelay: 750,
                trigger: (
                  <div className='course-name-display' tabIndex={0}>
                    {!noCoursesAvailable ? parsedCourseName : t('noCoursesAvailable')}
                  </div>
                ),
                wide: false
              })}
            </div>
            {showCarouselSliderNavButtons && (
              <button aria-label={t('nextCourse')}
                className={classNames('nav-button', 'next-button', {
                  'nav-button-wait': loading || isTransitioning
                })}
                onClick={goToNext}
                type='button'>
                <Image src={iconChevronRight} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ariaLabelContentCarousel = t('ariaLabelContentCarousel', { courseName: strippedCourseName });

  return (
    <div ref={studentCourseDrivenCarouselRef}
      aria-label={ariaLabelContentCarousel}
      className='StudentCourseDrivenCarousel dashboard-widget student-dashboard-widget-carousel red'
      tabIndex={0}>
      <div className='w-title' />
      {getBookCardContent()}
    </div>
  );
});

export default StudentCourseDrivenCarousel;

SatCoreRegister('StudentCourseDrivenCarousel', StudentCourseDrivenCarousel);
